import React, { useState } from 'react'
import { AlertTriangle, Clock, XCircle, CreditCard, ExternalLink, Loader2, Sparkles } from 'lucide-react'
import { toast } from 'react-toastify'

interface License {
  status: string
  expiresAt: string | null
  daysRemaining: number | null
  isBlocked: boolean
  subscriptionStatus: string
  hasStripeAccount: boolean
  planPreference: string | null
}

interface LicenseBannerProps {
  license: License | undefined
}

const PLAN_NAMES: Record<string, string> = {
  starter: 'Starter',
  smart:   'Smart',
  pro:     'Pro',
  elite:   'Elite',
}

const LicenseBanner: React.FC<LicenseBannerProps> = ({ license }) => {
  const [isLoading, setIsLoading] = useState(false)

  if (!license || license.status === 'active') return null

  const { hasStripeAccount, planPreference, daysRemaining, status } = license
  const planName = planPreference ? (PLAN_NAMES[planPreference] || planPreference) : null

  // ── Acción según tipo de cliente ──
  const handleAction = async () => {
    if (hasStripeAccount) {
      // Cliente con Stripe — abrir portal para actualizar tarjeta
      setIsLoading(true)
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/billing/portal', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        if (!response.ok) throw new Error('Error al acceder al portal')
        const data = await response.json()
        if (data.portalUrl) window.open(data.portalUrl, '_blank')
      } catch (error) {
        toast.error('No se pudo abrir el portal. Intenta de nuevo o contacta a soporte@bdctech.com.mx')
      } finally {
        setIsLoading(false)
      }
    } else {
      // Cliente sin Stripe — llevar a elegir plan
      window.location.href = '/dashboard/billing'
    }
  }

  // ── Configuración del banner según estado + tipo de cliente ──
  const getBannerConfig = () => {
    if (status === 'expiring_soon') {
      if (hasStripeAccount) {
        return {
          bgColor: 'bg-blue-500/10 border-blue-500/30',
          textColor: 'text-blue-400',
          icon: Clock,
          title: `Tu plan se renueva automáticamente en ${daysRemaining} día${daysRemaining !== 1 ? 's' : ''}`,
          message: 'El cargo se realizará a tu método de pago registrado.',
          buttonText: 'Ver facturación',
          buttonStyle: 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30',
        }
      } else {
        return {
          bgColor: 'bg-blue-500/10 border-blue-500/30',
          textColor: 'text-blue-400',
          icon: Clock,
          title: `Tu periodo de prueba termina en ${daysRemaining} día${daysRemaining !== 1 ? 's' : ''}`,
          message: planName
            ? `Elegiste el plan ${planName}. Actívalo ahora para no perder el acceso.`
            : 'Elige un plan para continuar usando InnovAgent sin interrupciones.',
          buttonText: planName ? `Activar plan ${planName}` : 'Elegir mi plan',
          buttonStyle: 'bg-blue-600 hover:bg-blue-700 text-white',
        }
      }
    }

    if (status === 'expired_grace') {
      const graceDays = daysRemaining ? Math.abs(daysRemaining) : 0
      const remainingGrace = Math.max(0, 4 - graceDays)
      if (hasStripeAccount) {
        return {
          bgColor: 'bg-orange-500/10 border-orange-500/30',
          textColor: 'text-orange-400',
          icon: AlertTriangle,
          title: 'No pudimos procesar tu pago',
          message: `Tienes ${remainingGrace} día${remainingGrace !== 1 ? 's' : ''} para actualizar tu método de pago antes de que tu cuenta sea suspendida.`,
          buttonText: 'Actualizar método de pago',
          buttonStyle: 'bg-orange-500 hover:bg-orange-600 text-white',
        }
      } else {
        return {
          bgColor: 'bg-orange-500/10 border-orange-500/30',
          textColor: 'text-orange-400',
          icon: AlertTriangle,
          title: 'Tu periodo de prueba terminó',
          message: planName
            ? `No pierdas tu información. Activa el plan ${planName} que elegiste para seguir usando la plataforma.`
            : `Tienes ${remainingGrace} día${remainingGrace !== 1 ? 's' : ''} antes de que tu cuenta sea suspendida. Elige un plan para continuar.`,
          buttonText: planName ? `Activar plan ${planName}` : 'Activar mi cuenta',
          buttonStyle: 'bg-orange-500 hover:bg-orange-600 text-white',
        }
      }
    }

    if (status === 'blocked') {
      if (hasStripeAccount) {
        return {
          bgColor: 'bg-red-500/10 border-red-500/30',
          textColor: 'text-red-400',
          icon: XCircle,
          title: '⚠️ Cuenta suspendida por fallo de pago',
          message: 'Actualiza tu información de pago para reactivar tu cuenta inmediatamente.',
          buttonText: 'Actualizar método de pago',
          buttonStyle: 'bg-red-500 hover:bg-red-600 text-white',
        }
      } else {
        return {
          bgColor: 'bg-red-500/10 border-red-500/30',
          textColor: 'text-red-400',
          icon: XCircle,
          title: '⚠️ Cuenta suspendida',
          message: planName
            ? `Activa el plan ${planName} para recuperar el acceso a tu cuenta y toda tu información.`
            : 'Activa un plan para recuperar el acceso a tu cuenta y toda tu información.',
          buttonText: planName ? `Recuperar con plan ${planName}` : 'Reactivar mi cuenta',
          buttonStyle: 'bg-red-500 hover:bg-red-600 text-white',
        }
      }
    }

    return null
  }

  const config = getBannerConfig()
  if (!config) return null

  const Icon = config.icon

  return (
    <div className={`mb-6 p-4 rounded-xl border ${config.bgColor} backdrop-blur-sm`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${config.bgColor}`}>
            <Icon className={`w-5 h-5 ${config.textColor}`} />
          </div>
          <div>
            <h4 className={`font-semibold ${config.textColor}`}>
              {config.title}
            </h4>
            <p className="text-sm text-slate-400">
              {config.message}
            </p>
          </div>
        </div>
        <button
          onClick={handleAction}
          disabled={isLoading}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${config.buttonStyle} disabled:opacity-50`}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : hasStripeAccount ? (
            <ExternalLink className="w-4 h-4" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          {isLoading ? 'Abriendo...' : config.buttonText}
        </button>
      </div>
    </div>
  )
}

export default LicenseBanner
